import * as FormSelectors from "../../../Rx/RxCoverageApplication/Selectors/form.selector";
import * as PaymentSelectors from "../../../Rx/RxCoverageApplication/Selectors/payment.selector";
import { useEffect } from "react";
import { Selectors } from "@jauntin/reactables";
import { ExtendedMeta, classname, mapExtendedErrors } from "@jauntin/utilities";
import { Field } from "@reactables/react-forms";
import { TextInput, TwoStageButton } from "@jauntin/react-ui";
import { useAppStore } from "@reactables/react";
import RadioInput from "Features/Shared/Components/RadioInput";
import AddressFieldGroup from "Features/Shared/Components/AddressFieldGroup";
import { AppState } from "Features/Shared/Rx/RxApp";
import { ContactType } from "Constants/contactTypes";
import { RxCoverageApplicationProp } from "../../../Rx/RxCoverageApplication/RxCoverageApplication";
import CardExpField from "./CardExpField";
import { useMicroform } from "./useMicroform";
import { Spinner, Alert } from "react-bootstrap";

const CCFields = ({
  rxCoverageApplication: [state, actions],
}: {
  rxCoverageApplication: RxCoverageApplicationProp;
}) => {
  useEffect(() => {
    actions.applicationForm.creditCardReset();
  }, [actions]);

  const [appState] = useAppStore<AppState>();
  const usStates = appState?.usStates.data;

  const { tokenizer, formLoading, sessionError } = useMicroform({
    numberContainerId: "card-number",
    securityCodeContainerId: "cvv",
    onFieldChange: actions.applicationForm.onMicroformFieldChange,
    onFieldBlur: actions.applicationForm.onMicroformFieldBlur,
  });

  if (!usStates) return;

  const hasError = PaymentSelectors.getCreditCardCheckError(state);
  const billingRef = `payment.billing`;
  const creditCardRef = `${billingRef}.creditCard`;
  const differentAddressRef = `${billingRef}.differentAddressFields`;

  const { payment } = FormSelectors.getRootValue(state);

  const enabled = FormSelectors.getControl(
    state,
    "insuranceContact.confirmed",
  ).touched;

  const ccGroupControl = FormSelectors.getControl(state, `${creditCardRef}`);

  const showErrors = ({ touched, error }: ExtendedMeta) =>
    Boolean((touched || state.touchButtons.payment) && error);

  const cardNumberValidControl = mapExtendedErrors(
    FormSelectors.getControl(state, `${creditCardRef}.cardNumberValid`),
    {
      messages: {
        microformFieldRequired: "Required",
        microformFieldInvalid: "Invalid credit card number",
      },
    },
  );
  const showCardNumberErrors = showErrors(cardNumberValidControl);

  const cvvValidControl = mapExtendedErrors(
    FormSelectors.getControl(state, `${creditCardRef}.cvvValid`),
    {
      messages: {
        microformFieldRequired: "Required",
        microformFieldInvalid: "Invalid CVV",
      },
    },
  );
  const showCvvErrors = showErrors(cvvValidControl);

  if (sessionError)
    return (
      <Alert variant="danger">
        <i className="fas fa-times-circle alert__icon" />
        <div className="c-charcoal">
          There was an error loading the payment form. Please refresh the page
          to try again or call{" "}
          <a href="tel:+1-844-747-6240" className="c-charcoal">
            (844) 747-6240
          </a>{" "}
          for assistance.
        </div>
      </Alert>
    );

  return (
    <>
      {formLoading && <Spinner animation="border" role="status" />}
      <div hidden={payment.confirmed || formLoading}>
        <div className="mb-1">
          We accept Visa, Mastercard, American Express or Discover.
        </div>
        <div className="form-group">
          {/* eslint-disable-next-line jsx-a11y/label-has-associated-control */}
          <label
            htmlFor="card-number"
            className={classname(
              "label",
              showCardNumberErrors && "form-error__label",
            )}
          >
            Card Number
          </label>
          {showCardNumberErrors && (
            <div className="form-error">{cardNumberValidControl.error}</div>
          )}
          <div
            className={classname(
              "form-control form-control-lg",
              showCardNumberErrors && "form-control--error",
            )}
            id="card-number"
          ></div>
        </div>
        <div className="form-group form-row">
          <Field
            component={TextInput}
            name={`${creditCardRef}.cardName`}
            label="Name on Card"
            ariaLabel="Credit Card Name"
            wrapperClassName="col-md mb-3 d-flex flex-column"
            inputClassName="form-control-lg"
            disabled={!enabled}
            showErrors={showErrors}
          />
          <Field
            component={CardExpField}
            name={`${creditCardRef}.expiryDate`}
            disabled={!enabled}
            showErrors={showErrors}
          />
          <div className="form-group col-auto d-flex flex-column">
            <label
              htmlFor="cvv"
              className={classname(
                "label",
                showCvvErrors && "form-error__label",
              )}
            >
              CVV
            </label>
            {showCvvErrors && (
              <div className="form-error">{cvvValidControl.error}</div>
            )}
            <div
              className={classname(
                "form-control form-control-lg mw--130",
                showCvvErrors && "form-control--error",
              )}
              id="cvv"
            ></div>
          </div>
        </div>
        <div className="form-group">
          <div className="form-group form-row">
            <div className="col-auto">
              <label htmlFor="fieldPayeeSame" className="label">
                Billing address same as contact
              </label>
            </div>
            <div className="col-auto my-auto">
              <div className="custom-control custom-radio custom-control-inline">
                <Field
                  name={`${billingRef}.sameAddressAsContact`}
                  component={RadioInput}
                  value="yes"
                  id={`${billingRef}.sameAddressAsContactYes`}
                  className="custom-control-input"
                  disabled={!enabled}
                  onChange={(e) => {
                    actions.applicationForm.selectSameAddressAsContact(
                      e.target.value,
                    );
                  }}
                />
                <label
                  className="custom-control-label label"
                  htmlFor={`${billingRef}.sameAddressAsContactYes`}
                >
                  Yes
                </label>
              </div>
            </div>
            <div className="col-auto my-auto">
              <div className="custom-control custom-radio custom-control-inline">
                <Field
                  name={`${billingRef}.sameAddressAsContact`}
                  component={RadioInput}
                  value="no"
                  id={`${billingRef}.sameAddressAsContactNo`}
                  className="custom-control-input"
                  onChange={(e) => {
                    actions.applicationForm.selectSameAddressAsContact(
                      e.target.value,
                    );
                  }}
                  disabled={!enabled}
                />
                <label
                  className="custom-control-label label"
                  htmlFor={`${billingRef}.sameAddressAsContactNo`}
                >
                  No
                </label>
              </div>
            </div>
          </div>
        </div>
        {payment.billing.sameAddressAsContact === "no" && (
          <div id="newPayeeInfo">
            <div className="form-group mt-4">
              <div className="custom-control custom-radio custom-control-inline">
                <Field
                  id={`${differentAddressRef}.typeInd`}
                  name={`${differentAddressRef}.type`}
                  className="custom-control-input"
                  component={RadioInput}
                  value={ContactType.Person}
                  disabled={!enabled}
                  onChange={() =>
                    actions.applicationForm.selectDifferentPaymentAddressContactType(
                      ContactType.Person,
                    )
                  }
                />
                <label
                  className="custom-control-label label"
                  htmlFor={`${differentAddressRef}.typeInd`}
                >
                  an individual
                </label>
              </div>
              <div className="custom-control custom-radio custom-control-inline">
                <Field
                  id={`${differentAddressRef}.typeCorp`}
                  name={`${differentAddressRef}.type`}
                  className="custom-control-input"
                  component={RadioInput}
                  value={ContactType.Company}
                  disabled={!enabled}
                  onChange={() =>
                    actions.applicationForm.selectDifferentPaymentAddressContactType(
                      ContactType.Company,
                    )
                  }
                />
                <label
                  className="custom-control-label label"
                  htmlFor={`${differentAddressRef}.typeCorp`}
                >
                  a company/organization
                </label>
              </div>
            </div>
            {payment.billing.differentAddressFields.type ===
              ContactType.Person && (
              <div id="payeeIndividual" className="form-row">
                <Field
                  component={TextInput}
                  name={`${differentAddressRef}.firstName`}
                  label="First name"
                  ariaLabel="First name"
                  placeholder="First name"
                  wrapperClassName="col-sm"
                  inputClassName="form-control-lg"
                  disabled={!enabled}
                  showErrors={showErrors}
                />
                <Field
                  component={TextInput}
                  name={`${differentAddressRef}.lastName`}
                  label="Last name"
                  ariaLabel="Last name"
                  placeholder="Last name"
                  wrapperClassName="col-sm"
                  inputClassName="form-control-lg"
                  disabled={!enabled}
                  showErrors={showErrors}
                />
              </div>
            )}
            {payment.billing.differentAddressFields.type ===
              ContactType.Company && (
              <div id="payeeCompany" className="form-row">
                <Field
                  component={TextInput}
                  name={`${differentAddressRef}.companyName`}
                  label="Company name"
                  ariaLabel="Company name"
                  placeholder="Company name"
                  wrapperClassName="col-sm"
                  inputClassName="form-control-lg"
                  disabled={!enabled}
                  showErrors={showErrors}
                />
              </div>
            )}
            <AddressFieldGroup
              groupName={`${differentAddressRef}.address`}
              usStates={usStates}
              disabled={!enabled}
              showErrors={showErrors}
            />
          </div>
        )}
        <hr />

        <div className="float-right">
          {hasError && (
            <div className="form-error">
              There was an error processing your credit card.
            </div>
          )}
          {(() => {
            const hasVisibleError =
              Selectors.hasControlTouchError(
                state.applicationForm,
                "payment.billing",
              ) ||
              (ccGroupControl.errors.creditCardCvv &&
                FormSelectors.getControl(state, `${creditCardRef}.cvv`)
                  .touched);

            const buttonTouched = state.touchButtons.payment;

            const billingValid = FormSelectors.getControl(
              state,
              billingRef,
            ).valid;

            const actionEnabled = buttonTouched || billingValid;

            const disabled =
              !enabled || hasVisibleError || (actionEnabled && !billingValid);

            return (
              <TwoStageButton
                spinning={PaymentSelectors.getIsCheckingCreditCard(state)}
                disabled={disabled}
                actionEnabled={actionEnabled}
                action={() => {
                  actions.applicationForm.confirmCreditCard({
                    normalizedCC:
                      PaymentSelectors.getNormalizedCreditCard(state),
                    tokenizer,
                  });
                }}
                touch={() => actions.touchButton("payment")}
                text="Confirm Payment Information"
                className="btn btn-primary d-flex"
              />
            );
          })()}
        </div>
      </div>
    </>
  );
};

export default CCFields;
